import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { checkAvailability } from '../../utils/supabaseExtended';

const CalendarContainer = styled.div`
  background: white;
  border: 2px solid #e1e5e9;
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0.5rem;
`;

const MonthYear = styled.h3`
  margin: 0;
  color: #004744;
  font-size: 1.1rem;
`;

const NavButton = styled.button`
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: #004744;
    color: white;
    border-color: #004744;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    &:hover {
      background: #f8f9fa;
      color: inherit;
      border-color: #dee2e6;
    }
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  margin-bottom: 1rem;
`;

const DayHeader = styled.div`
  padding: 0.5rem;
  text-align: center;
  font-weight: 600;
  font-size: 0.8rem;
  color: #666;
  background: #f8f9fa;
  border-radius: 4px;
`;

const DateCell = styled.button<{
  $isToday: boolean;
  $isSelected: boolean;
  $isInRange: boolean;
  $isAvailable: boolean;
  $isPast: boolean;
  $isOtherMonth: boolean;
}>`
  padding: 0.75rem 0.5rem;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s;
  position: relative;
  background: ${props => {
    if (props.$isPast || props.$isOtherMonth) return '#f8f9fa';
    if (!props.$isAvailable) return '#ffe6e6';
    if (props.$isSelected) return '#004744';
    if (props.$isInRange) return 'rgba(0, 71, 68, 0.12)';
    return 'white';
  }};
  
  color: ${props => {
    if (props.$isPast || props.$isOtherMonth) return '#ccc';
    if (!props.$isAvailable) return '#dc3545';
    if (props.$isSelected) return 'white';
    if (props.$isToday) return '#004744';
    return '#333';
  }};
  
  border-color: ${props => {
    if (props.$isSelected) return '#004744';
    if (props.$isToday) return '#004744';
    return '#e1e5e9';
  }};

  &:hover {
    ${props => {
      if (props.$isPast || props.$isOtherMonth || !props.$isAvailable) return '';
      return `
        background: rgba(0, 71, 68, 0.12);
        border-color: #004744;
        transform: translateY(-1px);
      `;
    }}
  }

  &:disabled {
    cursor: not-allowed;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 2px;
    left: 50%;
    transform: translateX(-50%);
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: ${props => {
      if (!props.$isAvailable && !props.$isPast && !props.$isOtherMonth) return '#dc3545';
      return 'transparent';
    }};
  }
`;

const Legend = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  font-size: 0.8rem;
  padding: 0.5rem;
  border-top: 1px solid #e1e5e9;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LegendColor = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: ${props => props.$color};
  border: 1px solid #ccc;
`;

// Helpers to avoid timezone shifts (no toISOString)
const formatLocalDate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const parseLocalDate = (s: string): Date => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

interface AvailabilityCalendarProps {
  accommodationId: string;
  selectedRange: {
    checkIn: string | null;
    checkOut: string | null;
  };
  onDateSelect: (checkIn: string, checkOut: string) => void;
  minDate?: string;
}

const AvailabilityCalendar: React.FC<AvailabilityCalendarProps> = ({
  accommodationId,
  selectedRange,
  onDateSelect,
  minDate
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availability, setAvailability] = useState<{ [date: string]: boolean }>({});
  const [loading, setLoading] = useState(false);
  const [selectingMode, setSelectingMode] = useState<'checkIn' | 'checkOut' | null>(null);

  const today = new Date();
  const minSelectDate = minDate ? parseLocalDate(minDate) : today;

  // Load availability for current month
  useEffect(() => {
    loadMonthAvailability();
  }, [currentMonth, accommodationId]);

  const loadMonthAvailability = async () => {
    if (!accommodationId) return;
    
    setLoading(true);
    try {
      const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
      
      const availabilityMap: { [date: string]: boolean } = {};
      
      // Check each day of the month
      for (let day = 1; day <= endOfMonth.getDate(); day++) {
        const checkDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
        const nextDay = new Date(checkDate);
        nextDay.setDate(nextDay.getDate() + 1);
        
        const result = await checkAvailability(
          accommodationId,
          formatLocalDate(checkDate),
          formatLocalDate(nextDay)
        );
        
        availabilityMap[formatLocalDate(checkDate)] = result.available;
      }
      
      setAvailability(availabilityMap);
    } catch (error) {
      console.error('Error loading availability:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      if (direction === 'prev') {
        newMonth.setMonth(newMonth.getMonth() - 1);
      } else {
        newMonth.setMonth(newMonth.getMonth() + 1);
      }
      return newMonth;
    });
  };

  const handleDateClick = (date: Date) => {
    const dateStr = formatLocalDate(date);
    
    // Don't allow selection of past dates or unavailable dates
    if (date < minSelectDate || !availability[dateStr]) {
      return;
    }

    if (!selectingMode || selectingMode === 'checkIn') {
      // Start new selection
      setSelectingMode('checkOut');
      onDateSelect(dateStr, '');
    } else if (selectingMode === 'checkOut') {
      const checkInDate = parseLocalDate(selectedRange.checkIn!);
      
      if (date <= checkInDate) {
        // If selected date is before or same as check-in, restart selection
        setSelectingMode('checkOut');
        onDateSelect(dateStr, '');
      } else {
        // Complete the range
        setSelectingMode(null);
        onDateSelect(selectedRange.checkIn!, dateStr);
      }
    }
  };

  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const startDate = new Date(firstDayOfMonth);
    startDate.setDate(startDate.getDate() - firstDayOfMonth.getDay());
    
    const days = [];
    const dayHeaders = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
    
    // Add day headers
    dayHeaders.forEach(day => (
      days.push(<DayHeader key={`header-${day}`}>{day}</DayHeader>)
    ));
    
    // Add calendar days
    for (let i = 0; i < 42; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      
      const dateStr = formatLocalDate(currentDate);
      const isToday = currentDate.toDateString() === today.toDateString();
      const isOtherMonth = currentDate.getMonth() !== month;
      const isPast = currentDate < minSelectDate;
      const isAvailable = availability[dateStr] !== false;
      const isSelected = selectedRange.checkIn === dateStr || selectedRange.checkOut === dateStr;
      
      // Check if date is in range
      let isInRange = false;
      if (selectedRange.checkIn && selectedRange.checkOut) {
        const checkIn = parseLocalDate(selectedRange.checkIn);
        const checkOut = parseLocalDate(selectedRange.checkOut);
        isInRange = currentDate > checkIn && currentDate < checkOut;
      }
      
      days.push(
        <DateCell
          key={dateStr}
          $isToday={isToday}
          $isSelected={isSelected}
          $isInRange={isInRange}
          $isAvailable={isAvailable}
          $isPast={isPast}
          $isOtherMonth={isOtherMonth}
          disabled={isPast || !isAvailable || isOtherMonth}
          onClick={() => handleDateClick(currentDate)}
        >
          {currentDate.getDate()}
        </DateCell>
      );
    }
    
    return days;
  };

  const monthNames = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ];

  const canGoPrev = currentMonth > new Date(today.getFullYear(), today.getMonth(), 1);

  return (
    <CalendarContainer>
      <CalendarHeader>
        <NavButton 
          onClick={() => navigateMonth('prev')}
          disabled={!canGoPrev}
        >
          ←
        </NavButton>
        <MonthYear>
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </MonthYear>
        <NavButton onClick={() => navigateMonth('next')}>
          →
        </NavButton>
      </CalendarHeader>
      
      <CalendarGrid>
        {loading ? (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem' }}>
            Caricamento disponibilità...
          </div>
        ) : (
          renderCalendar()
        )}
      </CalendarGrid>
      
      <Legend>
        <LegendItem>
          <LegendColor $color="rgba(0, 71, 68, 0.12)" />
          <span>Disponibile</span>
        </LegendItem>
        <LegendItem>
          <LegendColor $color="#ffe6e6" />
          <span>Non disponibile</span>
        </LegendItem>
        <LegendItem>
          <LegendColor $color="#004744" />
          <span>Selezionato</span>
        </LegendItem>
      </Legend>
      
      {selectingMode === 'checkOut' && (
        <div style={{ 
          textAlign: 'center', 
          padding: '0.5rem', 
          fontSize: '0.9rem', 
          color: '#004744',
          fontWeight: '500'
        }}>
          Seleziona la data di check-out
        </div>
      )}
    </CalendarContainer>
  );
};

export default AvailabilityCalendar;